import React, { useCallback, useEffect, useState } from 'react';
import './AdminIngestionPage.css';
import {
  adminIngestionService,
  IngestionJob,
  JobsPage,
  ListJobsParams,
  QueueStats,
  SourceType,
  TriggerJobPayload,
  JobStatus
} from '../../services/admin/admin-ingestion.service';
import AdminNavBar from './AdminNavBar';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SOURCE_TYPES: SourceType[] = [
  'tourism_site',
  'web_page',
  'youtube_video',
  'ai_result',
  'image',
  'destination_images',
  'destination_ai_summary',
  'destination_seasonality',
  'destination_activities',
  'destination_attractions',
  'destination_dining'
];

const JOB_STATUSES: JobStatus[] = [
  'queued',
  'in_progress',
  'completed',
  'failed',
  'dead_letter',
  'cancelled'
];

const STATUS_BADGE: Record<JobStatus, string> = {
  queued: 'secondary',
  in_progress: 'primary',
  completed: 'success',
  failed: 'danger',
  dead_letter: 'dark',
  cancelled: 'warning'
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function QueueStatsPanel({ stats }: { stats: QueueStats | null }) {
  if (!stats) return null;
  const { totals, queueDepth } = stats;
  return (
    <div className='card mb-4'>
      <div className='card-header fw-bold'>Queue Stats</div>
      <div className='card-body'>
        <div className='d-flex flex-wrap gap-3'>
          <Stat
            label='In-memory queue'
            value={queueDepth}
          />
          <Stat
            label='Queued (DB)'
            value={totals.queued ?? 0}
          />
          <Stat
            label='In Progress'
            value={totals.inProgress ?? 0}
          />
          <Stat
            label='Completed'
            value={totals.completed ?? 0}
          />
          <Stat
            label='Failed'
            value={totals.failed ?? 0}
          />
          <Stat
            label='Dead Letter'
            value={totals.deadLetter ?? 0}
            color='danger'
          />
          <Stat
            label='Cancelled'
            value={totals.cancelled ?? 0}
          />
        </div>
        <p className='text-muted mt-2 mb-0 admin-ingestion-stats-time'>
          As of {new Date(stats.capturedAt).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color?: string;
}) {
  return (
    <div className='admin-ingestion-stat-item'>
      <div className={`admin-ingestion-stat-value text-${color ?? 'dark'}`}>
        {value}
      </div>
      <div className='admin-ingestion-stat-label'>{label}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: JobStatus }) {
  return (
    <span className={`badge bg-${STATUS_BADGE[status] ?? 'secondary'}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Trigger Job Form
// ---------------------------------------------------------------------------

function TriggerJobForm({ onSuccess }: { onSuccess: () => void }) {
  const [mode, setMode] = useState<TriggerJobPayload['mode']>('free_form');
  const [sourceType, setSourceType] = useState<SourceType>('web_page');
  const [url, setUrl] = useState('');
  const [videoId, setVideoId] = useState('');
  const [promptText, setPromptText] = useState('');
  const [destinationIds, setDestinationIds] = useState('');
  const [researchId, setResearchId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [canCrawl, setCanCrawl] = useState(true);
  const [canStoreEmbeddings, setCanStoreEmbeddings] = useState(true);
  const [canUseForRetrieval, setCanUseForRetrieval] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const destIds = destinationIds
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const payload: TriggerJobPayload = {
        mode,
        sourceType: mode === 'free_form' ? sourceType : undefined,
        url: url || undefined,
        videoId: videoId || undefined,
        promptText: promptText || undefined,
        destinationIds: destIds.length ? destIds : undefined,
        researchId: researchId || undefined,
        sourceId: sourceId || undefined,
        usagePolicy: { canCrawl, canStoreEmbeddings, canUseForRetrieval },
        priority: 'high'
      };

      await adminIngestionService.triggerJob(payload);
      // Reset relevant fields
      setUrl('');
      setVideoId('');
      setPromptText('');
      setResearchId('');
      setSourceId('');
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message ?? 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='card mb-4'>
      <div className='card-header fw-bold'>Trigger Ingestion Job</div>
      <div className='card-body'>
        {error && <div className='alert alert-danger py-2'>{error}</div>}
        <form onSubmit={handleSubmit}>
          {/* Mode */}
          <div className='mb-3'>
            <label className='form-label'>Mode</label>
            <select
              className='form-select'
              title='Select trigger mode'
              value={mode}
              onChange={(e) =>
                setMode(e.target.value as TriggerJobPayload['mode'])
              }
            >
              <option value='free_form'>Free-form (URL / Video / AI)</option>
              <option value='by_ids'>
                By IDs (researchId / destinationId)
              </option>
              <option value='force_reingest'>Force Re-ingest (sourceId)</option>
            </select>
          </div>

          {/* Free-form fields */}
          {mode === 'free_form' && (
            <>
              <div className='mb-3'>
                <label className='form-label'>Source Type</label>
                <select
                  className='form-select'
                  title='Select source type'
                  value={sourceType}
                  onChange={(e) => setSourceType(e.target.value as SourceType)}
                >
                  {SOURCE_TYPES.map((t) => (
                    <option
                      key={t}
                      value={t}
                    >
                      {t.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              {(sourceType === 'web_page' ||
                sourceType === 'tourism_site' ||
                sourceType === 'image') && (
                <div className='mb-3'>
                  <label className='form-label'>URL</label>
                  <input
                    className='form-control'
                    type='url'
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder='https://example.com'
                  />
                </div>
              )}
              {sourceType === 'youtube_video' && (
                <div className='mb-3'>
                  <label className='form-label'>YouTube Video ID</label>
                  <input
                    className='form-control'
                    value={videoId}
                    onChange={(e) => setVideoId(e.target.value)}
                    placeholder='dQw4w9WgXcQ'
                  />
                </div>
              )}
              {sourceType === 'ai_result' && (
                <div className='mb-3'>
                  <label className='form-label'>Prompt Text</label>
                  <textarea
                    className='form-control'
                    rows={3}
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    placeholder='Enter AI prompt text...'
                  />
                </div>
              )}
              {/* Usage policy */}
              <div className='mb-3'>
                <label className='form-label d-block'>Usage Policy</label>
                <div className='form-check form-check-inline'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='canCrawl'
                    checked={canCrawl}
                    onChange={(e) => setCanCrawl(e.target.checked)}
                  />
                  <label
                    className='form-check-label'
                    htmlFor='canCrawl'
                  >
                    Can Crawl
                  </label>
                </div>
                <div className='form-check form-check-inline'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='canStore'
                    checked={canStoreEmbeddings}
                    onChange={(e) => setCanStoreEmbeddings(e.target.checked)}
                  />
                  <label
                    className='form-check-label'
                    htmlFor='canStore'
                  >
                    Can Store Embeddings
                  </label>
                </div>
                <div className='form-check form-check-inline'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='canRetrieve'
                    checked={canUseForRetrieval}
                    onChange={(e) => setCanUseForRetrieval(e.target.checked)}
                  />
                  <label
                    className='form-check-label'
                    htmlFor='canRetrieve'
                  >
                    Can Use For Retrieval
                  </label>
                </div>
              </div>
            </>
          )}

          {/* by_ids fields */}
          {mode === 'by_ids' && (
            <>
              <div className='mb-3'>
                <label className='form-label'>Research ID (optional)</label>
                <input
                  className='form-control'
                  value={researchId}
                  onChange={(e) => setResearchId(e.target.value)}
                  placeholder='MongoDB ObjectId'
                />
              </div>
            </>
          )}

          {/* force_reingest */}
          {mode === 'force_reingest' && (
            <div className='mb-3'>
              <label className='form-label'>Source ID</label>
              <input
                className='form-control'
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                placeholder='MongoDB ObjectId of SourceModel'
              />
            </div>
          )}

          {/* Destination IDs (all modes) */}
          {mode !== 'force_reingest' && (
            <div className='mb-3'>
              <label className='form-label'>
                Destination IDs (comma-separated, optional)
              </label>
              <input
                className='form-control'
                value={destinationIds}
                onChange={(e) => setDestinationIds(e.target.value)}
                placeholder='id1, id2, ...'
              />
            </div>
          )}

          <button
            type='submit'
            className='btn btn-primary'
            disabled={submitting}
          >
            {submitting ? 'Enqueueing…' : 'Enqueue Job'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job Detail Panel
// ---------------------------------------------------------------------------

function JobDetailPanel({
  jobId,
  onClose,
  onRetry,
  onCancel,
  onRetryTier3,
  onRetryFailedSections,
  onRunSingleSection
}: {
  jobId: string;
  onClose: () => void;
  onRetry: (jobId: string) => Promise<void>;
  onCancel: (jobId: string) => Promise<void>;
  onRetryTier3: (sourceId: string, reason?: string) => Promise<void>;
  onRetryFailedSections: (jobId: string) => Promise<void>;
  onRunSingleSection: (payload: {
    jobId: string;
    sectionKey: string;
    forceRefresh?: boolean;
    reason?: string;
  }) => Promise<void>;
}) {
  const [data, setData] = useState<{
    job: IngestionJob;
    source: any;
    aiSummarySections?: Array<{
      sectionKey: string;
      status: 'completed' | 'failed' | 'skipped';
      attemptCount: number;
      startedAt?: string;
      completedAt?: string;
      strategy?: 'direct' | 'two_step';
      provenanceLinks?: string[];
      validationErrors?: string[];
      parseError?: string;
    }>;
    aiSummaryUsageBySection?: Record<
      string,
      {
        callCount: number;
        promptTokens: number;
        completionTokens: number;
        totalTokens: number;
        thoughtsTokenCount: number;
        toolUsePromptTokenCount: number;
        toolUseCompletionTokenCount: number;
        finishReasons: string[];
      }
    >;
    runtimeLogs?: Array<{
      _id: string;
      level: 'info' | 'warn' | 'error' | 'debug';
      message: string;
      loggedAt: string;
    }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    adminIngestionService
      .getJobDetail(jobId)
      .then((d) => setData(d))
      .catch((err) => setActionError(err.message))
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading)
    return (
      <div className='text-center py-4'>
        <div className='spinner-border spinner-border-sm' />
      </div>
    );

  if (!data)
    return <div className='text-danger'>Failed to load job detail.</div>;

  const { job, source } = data;
  const failedSections = (data.aiSummarySections ?? []).filter(
    (section) => section.status === 'failed'
  );

  const handleRetry = async () => {
    setActionError(null);
    try {
      await onRetry(jobId);
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleCancel = async () => {
    setActionError(null);
    try {
      await onCancel(jobId);
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleTier3Retry = async () => {
    if (!job.sourceId) {
      setActionError('This job has no sourceId, cannot request Tier 3 retry.');
      return;
    }

    setActionError(null);
    try {
      const reason = window.prompt(
        'Optional reason for Tier 3 retry (visible in audit log):',
        'manual admin retry'
      );
      await onRetryTier3(job.sourceId, reason ?? undefined);
    } catch (err: any) {
      setActionError(err?.message ?? 'Tier 3 retry request failed');
    }
  };

  const handleRetryFailedSections = async () => {
    setActionError(null);
    try {
      await onRetryFailedSections(jobId);
    } catch (err: any) {
      setActionError(err?.message ?? 'Failed section retry request failed');
    }
  };

  const handleRunSingleSection = async () => {
    const sectionKey = window.prompt(
      'Section key to run (identity_profile, climate_timing, experiences_outdoors, practical_logistics, culture_intelligence, media):',
      'identity_profile'
    );
    if (!sectionKey || !sectionKey.trim()) return;

    const forceRefresh = window.confirm(
      'Force refresh this section and bypass freshness cache?'
    );
    const reason = window.prompt(
      'Optional reason for audit trail:',
      'manual section troubleshooting'
    );

    setActionError(null);
    try {
      await onRunSingleSection({
        jobId,
        sectionKey: sectionKey.trim(),
        forceRefresh,
        reason: reason ?? undefined
      });
    } catch (err: any) {
      setActionError(err?.message ?? 'Single-section run request failed');
    }
  };

  return (
    <div className='card mb-4'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <span className='fw-bold'>
          Job Detail — <code className='admin-ingestion-job-id'>{job._id}</code>
        </span>
        <button
          className='btn btn-sm btn-outline-secondary'
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <div className='card-body'>
        {actionError && (
          <div className='alert alert-danger py-2'>{actionError}</div>
        )}

        {/* Meta */}
        <table className='table table-sm table-borderless mb-3 admin-ingestion-detail-table'>
          <tbody>
            <tr>
              <th>Status</th>
              <td>
                <StatusBadge status={job.status} />
              </td>
            </tr>
            <tr>
              <th>Source Type</th>
              <td>{job.sourceType}</td>
            </tr>
            <tr>
              <th>Priority</th>
              <td>{job.priority}</td>
            </tr>
            <tr>
              <th>AI Run Mode</th>
              <td>{job.aiSummaryRunMode || 'full'}</td>
            </tr>
            <tr>
              <th>Run Reason</th>
              <td>{job.aiSummaryRunReason || '—'}</td>
            </tr>
            <tr>
              <th>Destination</th>
              <td>{job.destinationName || '—'}</td>
            </tr>
            <tr>
              <th>Research</th>
              <td>{job.researchLabel || job.researchTitle || '—'}</td>
            </tr>
            <tr>
              <th>Attempts</th>
              <td>
                {job.attempts}/{job.maxAttempts}
              </td>
            </tr>
            {job.error && (
              <tr>
                <th>Error</th>
                <td className='text-danger'>{job.error}</td>
              </tr>
            )}
            {job.nextRetryAt && (
              <tr>
                <th>Next Retry</th>
                <td>{new Date(job.nextRetryAt).toLocaleString()}</td>
              </tr>
            )}
            <tr>
              <th>Created</th>
              <td>{new Date(job.createdAt).toLocaleString()}</td>
            </tr>
            {job.completedAt && (
              <tr>
                <th>Completed</th>
                <td>{new Date(job.completedAt).toLocaleString()}</td>
              </tr>
            )}
            {source && (
              <tr>
                <th>Source URL</th>
                <td>
                  <a
                    href={(source as any).url}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {(source as any).url}
                  </a>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Actions */}
        <div className='d-flex gap-2 mb-3'>
          {job.sourceType === 'youtube_video' &&
            !!job.sourceId &&
            (job.status === 'failed' || job.status === 'dead_letter') && (
              <button
                className='btn btn-sm btn-outline-primary'
                onClick={handleTier3Retry}
              >
                Retry Tier 3
              </button>
            )}
          {(job.status === 'failed' || job.status === 'dead_letter') && (
            <button
              className='btn btn-sm btn-warning'
              onClick={handleRetry}
            >
              Retry
            </button>
          )}
          {job.sourceType === 'destination_ai_summary' &&
            failedSections.length > 0 && (
              <button
                className='btn btn-sm btn-outline-warning'
                onClick={handleRetryFailedSections}
              >
                Retry Failed Sections
              </button>
            )}
          {job.sourceType === 'destination_ai_summary' && (
            <button
              className='btn btn-sm btn-outline-info'
              onClick={handleRunSingleSection}
            >
              Run One Section
            </button>
          )}
          {(job.status === 'queued' || job.status === 'failed') && (
            <button
              className='btn btn-sm btn-outline-danger'
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
        </div>

        {(data.aiSummarySections ?? []).length > 0 && (
          <>
            <h6 className='mt-3'>AI Summary Sections</h6>
            <div className='admin-ingestion-event-log'>
              {(data.aiSummarySections ?? []).map((section) => {
                const usage =
                  data.aiSummaryUsageBySection?.[section.sectionKey];
                return (
                  <div key={section.sectionKey}>
                    <div>
                      <strong>{section.sectionKey}</strong> [{section.status}]
                      attempts={section.attemptCount} strategy=
                      {section.strategy || '—'}
                    </div>
                    <div className='text-muted'>
                      started=
                      {section.startedAt
                        ? new Date(section.startedAt).toLocaleString()
                        : '—'}{' '}
                      completed=
                      {section.completedAt
                        ? new Date(section.completedAt).toLocaleString()
                        : '—'}
                    </div>
                    {usage && (
                      <div className='text-muted'>
                        calls={usage.callCount} tokens={usage.promptTokens}/
                        {usage.completionTokens}/{usage.totalTokens} thoughts=
                        {usage.thoughtsTokenCount} tool-use=
                        {usage.toolUsePromptTokenCount}/
                        {usage.toolUseCompletionTokenCount} finish=
                        {usage.finishReasons.join(', ') || '—'}
                      </div>
                    )}
                    {section.parseError && (
                      <div className='text-danger'>{section.parseError}</div>
                    )}
                    {section.validationErrors &&
                      section.validationErrors.length > 0 && (
                        <div className='text-warning'>
                          validation: {section.validationErrors.join(' | ')}
                        </div>
                      )}
                    {section.provenanceLinks &&
                      section.provenanceLinks.length > 0 && (
                        <div className='d-flex gap-2 my-1'>
                          {section.provenanceLinks.map((link) => (
                            <a
                              key={`${section.sectionKey}-${link}`}
                              href={link}
                              target='_blank'
                              rel='noreferrer'
                              className='btn btn-sm btn-outline-secondary'
                            >
                              Source
                            </a>
                          ))}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Event log */}
        <h6 className='mt-3'>Event Log</h6>
        <div className='admin-ingestion-event-log'>
          {(job.events ?? []).length === 0 ? (
            <span className='text-muted'>No events recorded.</span>
          ) : (
            [...(job.events ?? [])].reverse().map((ev, i) => (
              <div
                key={i}
                className={`text-${ev.level === 'error' ? 'danger' : ev.level === 'warn' ? 'warning' : 'dark'}`}
              >
                <span className='text-muted me-2'>
                  {new Date(ev.at).toLocaleTimeString()}
                </span>
                [{ev.level.toUpperCase()}] {ev.message}
              </div>
            ))
          )}
        </div>

        <h6 className='mt-3'>Runtime Logs (persisted)</h6>
        <div className='admin-ingestion-event-log'>
          {(data.runtimeLogs ?? []).length === 0 ? (
            <span className='text-muted'>No runtime logs recorded.</span>
          ) : (
            (data.runtimeLogs ?? []).map((ev) => (
              <div
                key={ev._id}
                className={`text-${ev.level === 'error' ? 'danger' : ev.level === 'warn' ? 'warning' : 'dark'}`}
              >
                <span className='text-muted me-2'>
                  {new Date(ev.loggedAt).toLocaleTimeString()}
                </span>
                <span>[{ev.level.toUpperCase()}]</span> {ev.message}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jobs List
// ---------------------------------------------------------------------------

function JobsListPanel({
  onSelectJob,
  onRetryTier3
}: {
  onSelectJob: (jobId: string) => void;
  onRetryTier3: (sourceId: string, reason?: string) => Promise<void>;
}) {
  const [page, setPage] = useState<JobsPage | null>(null);
  const [filters, setFilters] = useState<ListJobsParams>({
    page: 1,
    limit: 20
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminIngestionService
      .listJobs(filters)
      .then(setPage)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange = (key: keyof ListJobsParams, value: string) => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      [key]: value || undefined
    }));
  };

  return (
    <div className='card mb-4'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <span className='fw-bold'>Jobs</span>
        <button
          className='btn btn-sm btn-outline-secondary'
          onClick={load}
        >
          ↺ Refresh
        </button>
      </div>
      <div className='card-body p-2'>
        {/* Filters */}
        <div className='d-flex flex-wrap gap-2 mb-3 px-1'>
          <select
            className='form-select form-select-sm admin-ingestion-filter-status'
            title='Filter by status'
            value={filters.status ?? ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value=''>All statuses</option>
            {JOB_STATUSES.map((s) => (
              <option
                key={s}
                value={s}
              >
                {s.replace('_', ' ')}
              </option>
            ))}
          </select>

          <select
            className='form-select form-select-sm admin-ingestion-filter-type'
            title='Filter by source type'
            value={filters.sourceType ?? ''}
            onChange={(e) => handleFilterChange('sourceType', e.target.value)}
          >
            <option value=''>All source types</option>
            {SOURCE_TYPES.map((t) => (
              <option
                key={t}
                value={t}
              >
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>

          <input
            className='form-control form-control-sm admin-ingestion-filter-dest'
            placeholder='Destination ID filter'
            value={filters.destinationId ?? ''}
            onChange={(e) =>
              handleFilterChange('destinationId', e.target.value)
            }
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className='text-center py-3'>
            <div className='spinner-border spinner-border-sm' />
          </div>
        ) : error ? (
          <div className='text-danger p-2'>{error}</div>
        ) : (
          <>
            <table className='table table-sm table-hover mb-2 admin-ingestion-jobs-table'>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Destination</th>
                  <th>Research</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(page?.jobs ?? []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className='text-center text-muted py-3'
                    >
                      No jobs found.
                    </td>
                  </tr>
                ) : (
                  (page?.jobs ?? []).map((job) => (
                    <tr key={job._id}>
                      <td>
                        <code className='admin-ingestion-job-id-short'>
                          {job._id.slice(-8)}
                        </code>
                      </td>
                      <td>{job.sourceType.replace(/_/g, ' ')}</td>
                      <td>{job.destinationName || '—'}</td>
                      <td>{job.researchLabel || '—'}</td>
                      <td>
                        <StatusBadge status={job.status} />
                      </td>
                      <td>
                        {job.attempts}/{job.maxAttempts}
                      </td>
                      <td>{new Date(job.createdAt).toLocaleString()}</td>
                      <td>
                        <button
                          className='btn btn-link btn-sm p-0'
                          onClick={() => onSelectJob(job._id)}
                        >
                          Detail
                        </button>
                        {job.sourceType === 'youtube_video' &&
                          !!job.sourceId &&
                          (job.status === 'failed' ||
                            job.status === 'dead_letter') && (
                            <button
                              className='btn btn-link btn-sm p-0 ms-2 text-primary'
                              onClick={async () => {
                                const reason = window.prompt(
                                  'Optional reason for Tier 3 retry:',
                                  'manual admin retry'
                                );
                                await onRetryTier3(
                                  job.sourceId!,
                                  reason ?? undefined
                                );
                              }}
                            >
                              Retry Tier 3
                            </button>
                          )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {page && page.pagination.pages > 1 && (
              <div className='d-flex gap-2 align-items-center px-1'>
                <button
                  className='btn btn-sm btn-outline-secondary'
                  disabled={(filters.page ?? 1) <= 1}
                  onClick={() =>
                    setFilters((p) => ({ ...p, page: (p.page ?? 1) - 1 }))
                  }
                >
                  ‹ Prev
                </button>
                <span className='admin-ingestion-pagination-info'>
                  {filters.page ?? 1} / {page.pagination.pages}
                </span>
                <button
                  className='btn btn-sm btn-outline-secondary'
                  disabled={(filters.page ?? 1) >= page.pagination.pages}
                  onClick={() =>
                    setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }))
                  }
                >
                  Next ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminIngestionPage() {
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [listKey, setListKey] = useState(0); // bump to force JobsListPanel refresh

  useEffect(() => {
    adminIngestionService.getQueueStats().then(setStats).catch(console.error);
  }, []);

  const refreshAll = () => {
    adminIngestionService.getQueueStats().then(setStats).catch(console.error);
    setListKey((k) => k + 1);
    setSelectedJobId(null);
  };

  const handleRetry = async (jobId: string) => {
    await adminIngestionService.retryJob(jobId);
    refreshAll();
  };

  const handleCancel = async (jobId: string) => {
    await adminIngestionService.cancelJob(jobId);
    refreshAll();
  };

  const handleRetryFailedSections = async (jobId: string) => {
    await adminIngestionService.retryFailedSections(jobId);
    refreshAll();
  };

  const handleRunSingleSection = async (payload: {
    jobId: string;
    sectionKey: string;
    forceRefresh?: boolean;
    reason?: string;
  }) => {
    await adminIngestionService.runSingleSection(payload.jobId, {
      sectionKey: payload.sectionKey,
      forceRefresh: payload.forceRefresh,
      reason: payload.reason
    });
    refreshAll();
  };

  const handleRetryTier3 = async (sourceId: string, reason?: string) => {
    await adminIngestionService.retryYouTubeTier3(sourceId, reason);
    refreshAll();
  };

  return (
    <div className='container-fluid py-4 admin-ingestion-page'>
      <AdminNavBar />
      <h4 className='mb-4 fw-bold'>Ingestion Management</h4>

      <QueueStatsPanel stats={stats} />

      <TriggerJobForm onSuccess={refreshAll} />

      {selectedJobId && (
        <JobDetailPanel
          jobId={selectedJobId}
          onClose={() => setSelectedJobId(null)}
          onRetry={handleRetry}
          onCancel={handleCancel}
          onRetryTier3={handleRetryTier3}
          onRetryFailedSections={handleRetryFailedSections}
          onRunSingleSection={handleRunSingleSection}
        />
      )}

      <JobsListPanel
        key={listKey}
        onSelectJob={setSelectedJobId}
        onRetryTier3={handleRetryTier3}
      />
    </div>
  );
}
